import { useEffect, useMemo } from 'react';

import { fg } from '@atlaskit/platform-feature-flags/fg';

const PRELOAD_MAX_AGE = 5 * 60 * 1000; // 5 mins
const TIME_TO_INTENT = 200;

export type EntryPointPreloadActions<T> = {
	/** Cancel any pending preload and dispose the preloaded reference. */
	cancelPreload: () => void;
	/**
	 * Eagerly load the entry point immediately (cancels any pending preload timeouts).
	 * Returns the loaded reference wrapper with a `dispose` function.
	 */
	eagerLoad: () => { dispose: () => void; reference: T };
	/** Start preloading with an intent delay (TIME_TO_INTENT ms). No-op if already preloading. */
	preloadReference: () => void;
};

/**
 * Low-level hook that manages the preload / eager-load lifecycle for an entry
 * point reference.  It does **not** bind any DOM events — callers wire the
 * returned callbacks to whatever interaction pattern they need (e.g. JSX event
 * props, a ref-callback with `bind-event-listener`, etc.).
 *
 * This is the shared core extracted from `usePressablePreloadRef` so the same
 * logic can be reused in contexts that don't need the ref-callback pattern.
 */
export function useEntryPointPreload<T extends { dispose: () => void }>(
	load: () => T,
): EntryPointPreloadActions<T> {
	const actions = useMemo(() => {
		const request: {
			load?: { dispose: () => void; reference: T };
			preload?: { dispose: () => void; reference: T };
			preloadIntentTimeout?: ReturnType<typeof setTimeout>;
			preloadMaxAgeTimeout?: ReturnType<typeof setTimeout>;
		} = {};

		const clearPreloadTimeouts = () => {
			if (typeof request.preloadIntentTimeout === 'number') {
				clearTimeout(request.preloadIntentTimeout);
				delete request.preloadIntentTimeout;
			}

			if (
				typeof request.preloadMaxAgeTimeout === 'number' &&
				fg('platform_relay_preload_ensure_max_age_disposal') === false
			) {
				clearTimeout(request.preloadMaxAgeTimeout);
				delete request.preloadMaxAgeTimeout;
			}
		};

		const cancelPreload = () => {
			clearPreloadTimeouts();
		};

		const cleanupPreload = () => {
			request.preload?.dispose();

			// Only remove the max age timeout when the component is cleaned up.
			if (
				typeof request.preloadMaxAgeTimeout === 'number' &&
				fg('platform_relay_preload_ensure_max_age_disposal')
			) {
				clearTimeout(request.preloadMaxAgeTimeout);
				delete request.preloadMaxAgeTimeout;
			}
		};

		const loadReference = () => {
			if (request.load) {
				return request.load;
			}

			const reference = request.preload?.reference ?? load();
			if (request.preload) {
				delete request.preload;
			}

			request.load = {
				reference,
				dispose() {
					reference.dispose();
					delete request.load;
				},
			};

			return request.load;
		};

		const eagerLoad = () => {
			clearPreloadTimeouts();
			return loadReference();
		};

		const preloadReference = () => {
			if (request.preloadIntentTimeout || request.preload) {
				return;
			}

			request.preloadIntentTimeout = setTimeout(() => {
				delete request.preloadIntentTimeout;

				const reference = load();

				request.preload = {
					reference,
					dispose() {
						reference.dispose();
						delete request.preload;
					},
				};

				request.preloadMaxAgeTimeout = setTimeout(() => {
					delete request.preloadMaxAgeTimeout;
					request.preload?.dispose();
				}, PRELOAD_MAX_AGE);
			}, TIME_TO_INTENT);
		};

		return {
			preloadReference,
			cancelPreload,
			cleanupPreload,
			eagerLoad,
		};
	}, [load]);

	// Dispose preload and clear timeouts when component unmounts
	// or when `load` identity changes (new actions object created).
	useEffect(() => {
		return () => {
			actions.cleanupPreload();
		};
	}, [actions]);

	return actions;
}
